/**
 * Contracts for module "partners". Owner: partners.
 * Partner applications, staff decisions, verification checklist, onboarding (agreement + billing contract)
 * and customer consent grants. Import as `@qm/contracts/partners`.
 */
import { z } from 'zod';

export const PARTNER_TERMS_VERSION = '2026-09';
export const PARTNER_AGREEMENT_VERSION = '2026-09';
/** Privacy notice shown on the application form (NFR X-14: processing regions documented per data class). */
export const PARTNER_PRIVACY_NOTICE_VERSION = '2026-09';

export const PartnerStatusSchema = z.enum(['pending', 'approved', 'rejected', 'suspended']);
export type PartnerStatus = z.infer<typeof PartnerStatusSchema>;

const domain = z.string().trim().toLowerCase().min(3).max(253).regex(/^(?!-)[a-z0-9-]+(\.[a-z0-9-]+)+$/, 'invalid domain');

export const PartnerApplicationSchema = z.object({
  companyName: z.string().trim().min(2).max(160),
  /** Legacy clients may still submit this; new forms omit it. Metadata is inferred by the server. */
  companyDomain: domain.optional(),
  website: z.string().trim().url().max(2048).refine(v => { try { const u=new URL(v); return ['https:', 'http:'].includes(u.protocol) && !u.username && !u.password; } catch { return false; } }, 'Invalid website').optional(),
  country: z.string().trim().length(2).toUpperCase(),
  registrationNumber: z.string().trim().max(80).optional(),
  city: z.string().trim().min(1).max(120).optional(),
  designation: z.string().trim().min(1).max(120).optional(),
  clientCount: z.number().int().min(0).max(1000000).optional(),
  contactName: z.string().trim().min(2).max(120),
  contactEmail: z.string().trim().toLowerCase().email().max(254),
  contactPhone: z.string().trim().regex(/^\+?[0-9 ()-]{6,24}$/).optional(),
  clientCountBand: z.enum(['1-5', '6-20', '21-50', '51+']).optional(),
  services: z.array(z.string().trim().min(2).max(60)).max(20).default([]),
  acceptTerms: z.literal(true),
  termsVersion: z.literal(PARTNER_TERMS_VERSION),
  privacyNoticeVersion: z.literal(PARTNER_PRIVACY_NOTICE_VERSION),
}).strict();
export type PartnerApplicationInput = z.infer<typeof PartnerApplicationSchema>;

/** Self-service profile edits. Status is never accepted here (QM-NC01-002: approval only via staff action). */
export const PartnerProfileUpdateSchema = z.object({
  companyName: z.string().trim().min(2).max(160).optional(),
  website: z.string().trim().url().max(300).optional(),
  contactName: z.string().trim().min(2).max(120).optional(),
  contactPhone: z.string().trim().regex(/^\+?[0-9 ()-]{6,24}$/).optional(),
  services: z.array(z.string().trim().min(2).max(60)).max(20).optional(),
});

export const ReasonSchema = z.object({ reason: z.string().trim().min(5).max(500) });

export const CHECKLIST_ITEMS = ['company_registration', 'contact_verified', 'terms_accepted'] as const;
export const ChecklistItemSchema = z.enum(CHECKLIST_ITEMS);
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;
export const ChecklistUpdateSchema = z.object({ checked: z.boolean(), note: z.string().trim().max(500).optional() });

/**
 * Staff finding: is the applicant a marketing company (sells marketing services to third parties)? Required before
 * "ready" and approval. Only marketing companies (true) can be approved as partners; false -> the application can only
 * be converted to a customer.
 */
export const MarketingFindingSchema = z.object({
  isMarketingCompany: z.boolean(),
  note: z.string().trim().min(5).max(1000),
});
export type MarketingFinding = z.infer<typeof MarketingFindingSchema>;

export const AgreementAcceptSchema = z.object({ accept: z.literal(true), agreementVersion: z.literal(PARTNER_AGREEMENT_VERSION) });
export const BillingContractSchema = z.object({
  billingModel: z.enum(['quixo_billed', 'agency_billed']),
  legalName: z.string().trim().min(2).max(160),
  billingEmail: z.string().trim().toLowerCase().email(),
  currency: z.enum(['USD', 'AED']),
  taxId: z.string().trim().max(40).optional(),
  /** statement descriptor for agency-billed invoices; validated by assertTrueDescriptor (workspace contracts) */
  descriptor: z.string().trim().min(5).max(22).optional(),
});

/** Scopes a customer can grant to an agency (QM-B09-003). */
export const GRANT_SCOPES = ['read:workspace', 'write:content', 'manage:campaigns', 'manage:whatsapp', 'read:reports', 'manage:billing'] as const;
export const GrantScopeSchema = z.enum(GRANT_SCOPES);
export const DEFAULT_AGENCY_SCOPES: readonly (typeof GRANT_SCOPES)[number][] = ['read:workspace', 'write:content', 'manage:campaigns', 'manage:whatsapp', 'read:reports'];

export const AccessRequestSchema = z.object({
  tenantId: z.string().min(1).max(64),
  scopes: z.array(GrantScopeSchema).min(1).max(GRANT_SCOPES.length),
  message: z.string().trim().max(500).optional(),
});
export const GrantAcceptSchema = z.object({ scopes: z.array(GrantScopeSchema).min(1).max(GRANT_SCOPES.length) });
export const GrantRevokeSchema = z.object({ reason: z.string().trim().max(500).optional() });

export const PARTNER_EVENTS = {
  applicationSubmitted: 'partners.application.submitted',
  approved: 'partners.partner.approved',
  rejected: 'partners.partner.rejected',
  suspended: 'partners.partner.suspended',
  grantRevoked: 'partners.grant.revoked',
} as const;

/** Error `details.reason` values the portal maps to i18n keys `partners.errors.<reason>`. */
export const PARTNER_DENIAL_REASONS = ['partner_pending', 'partner_suspended', 'partner_rejected', 'agreement_required', 'checklist_incomplete', 'application_under_review'] as const;
